<template>
  <div class="call-result-type-select-container">
    <BSelect
      :model-value="selectValue"
      :items="items"
      item-text="name"
      item-value="value"
      fit
      :loading="loading"
      @update:model-value="handleUpdateModelValue"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, defineModel } from 'vue';
import { useI18n } from 'vue-i18n';
import { CustomCallResultTypeSummary, DefaultCallResultTypeSummary } from '@/api/openapi';
import { CallResultTypeApiService } from '@/api/user/resources/call_result_type';
import { useWait } from '@/composable/vue-wait';

type TModelValueType = 'custom' | 'default';

const i18n = useI18n();

const VALUE_ALL = 'all';
const CUSTOM_PREFIX = 'custom_';
const DEFAULT_PREFIX = 'default_';

const defaultCallResultTypeId = defineModel<number | null>('defaultCallResultTypeId', { default: null });
const customCallResultTypeId = defineModel<number | null>('customCallResultTypeId', { default: null });

const customCallResultTypes = ref<CustomCallResultTypeSummary[]>([]);
const defaultCallResultTypes = ref<DefaultCallResultTypeSummary[]>([]);

const { doActionWithWait, wait } = useWait();

const loading = computed(() => {
  return wait.is('fetchCallResultTypeSummaries');
});

const items = computed(() => {
  return [
    {
      value: VALUE_ALL,
      name: i18n.t('callResult.allCallResultTypes'),
    },
    ...defaultCallResultTypes.value.map((defaultCallResultType) => ({
      value: buildModelValue(defaultCallResultType.id, 'default'),
      name: defaultCallResultType.name,
    })),
    ...customCallResultTypes.value.map((customCallResultType) => ({
      value: buildModelValue(customCallResultType.id, 'custom'),
      name: customCallResultType.name,
    })),
  ];
});

const selectValue = computed<string | null>(() => {
  if (loading.value) return VALUE_ALL;
  if (customCallResultTypeId.value != null) {
    return buildModelValue(customCallResultTypeId.value, 'custom');
  }
  if (defaultCallResultTypeId.value != null) {
    return buildModelValue(defaultCallResultTypeId.value, 'default');
  }
  return VALUE_ALL;
});

const fetchCallResultTypeSummaries = async () => {
  await doActionWithWait('fetchCallResultTypeSummaries', async () => {
    const api = new CallResultTypeApiService();
    const response = await api.getCallResultTypeSummaries();
    customCallResultTypes.value = response.data.customCallResultTypes;
    defaultCallResultTypes.value = response.data.defaultCallResultTypes;
  });
};

const buildModelValue = (value: number, type: TModelValueType) => {
  if (value == null) return null;
  if (type === 'custom') {
    return `${CUSTOM_PREFIX}${value}`;
  } else if (type === 'default') {
    return `${DEFAULT_PREFIX}${value}`;
  }
  return null;
};

const parseModelValue = (value: string): { value: number | null, type: TModelValueType | null } => {
  if (value?.startsWith(CUSTOM_PREFIX)) {
    return {
      value: Number(value.substring(CUSTOM_PREFIX.length)),
      type: 'custom',
    };
  } else if (value?.startsWith(DEFAULT_PREFIX)) {
    return {
      value: Number(value.substring(DEFAULT_PREFIX.length)),
      type: 'default',
    };
  }
  return {
    value: null,
    type: null,
  };
};

const handleUpdateModelValue = (value: string | null) => {
  const { value: id, type } = parseModelValue(value);
  const newDefaultCallResultTypeId = type === 'default' ? id : null;
  const newCustomCallResultTypeId = type === 'custom' ? id : null;
  defaultCallResultTypeId.value = newDefaultCallResultTypeId;
  customCallResultTypeId.value = newCustomCallResultTypeId;
};

onMounted(() => {
  fetchCallResultTypeSummaries();
});
</script>

<style lang="scss" scoped>
.call-result-type-select-container {
  width: 100%;
}
</style>
